// MA02 상품 저장/삭제/엑셀 — 2.0 API (LOGISPRD-9613/9614/9615)
// 1.0 실기능 보존: 그리드 행 추가(+)=생성 POST(옵션코드 동시생성), 행 삭제(-)=DELETE, 다운로드=서버 xlsx(EXPORT 감사)
import { useMutation } from '@tanstack/react-query'
import request, { getExcel } from '@/utils/request'
import { ProductManagementParams, ProductManagementRow } from './useGetProductManagementList'
import { buildSearchQuery } from './searchQuery'

interface ProductBodyBase {
  barcode: string
  companyName: string | null
  uid: string | null
  unitCode: string | null
  productImageUrl: string | null
  itemCode: string | null
  itemOption: string | null
  itemName: string | null
  category: string | null
  color: string | null
  styleNumber: string | null
  brandName: string | null
  itemAttribute: string | null
  receivablePrice: number | null
  payablePrice: number | null
}

const toBody = (row: ProductManagementRow): ProductBodyBase => ({
  barcode: row.BARCODE,
  // 화면 비편집 필드는 원본 값 그대로 전송 — PUT 전체 교체 대비 데이터 보존
  companyName: row._companyName,
  uid: row.UID || null,
  unitCode: row._unitCode,
  productImageUrl: row.product_image_url || null,
  itemCode: row.GDS_CD || null,
  itemOption: row.GDS_OPT || null,
  itemName: row.GDS_NM || null,
  category: row.GDS_GROUP1 || null,
  color: row.GDS_COLOR || null,
  styleNumber: row.STL_NO || null,
  brandName: row.BRD_NM || null,
  itemAttribute: row.GDS_ATTR1 || null,
  receivablePrice: row.AR_PRICE,
  payablePrice: row.AP_PRICE,
})

export interface SaveTarget extends ProductManagementRow {
  _isNew?: boolean
}

/** 행별 저장 결과 — index 는 입력 rows 순서. 부분 실패 시에도 성공 행의 확정 itemId 를 유실하지 않는다 */
export interface SaveRowResult {
  index: number
  ok: boolean
  /** 성공 시 확정 itemId (신규 행은 POST 응답 itemId) — 행 _id 갱신용 */
  id: number
  /** 실패 사유 (호출부에서 행 식별과 함께 표시) */
  error?: unknown
}

// 신규 행(그리드 + 추가)은 POST 생성(shipperCode 필수, 전체 메뉴… 아님 — 옵션코드 목록은 1.0 파리티로 BE 가 동시 생성),
// 기존 행은 PUT /{itemId} 수정 (1.0 grid/save I/E 배치 대응)
export const useSaveProducts = () =>
  useMutation({
    // 행별 try/catch 로 결과를 수집한다 — 중간 실패가 예외로 전파되면 이미 성공한 POST 의 itemId 가 유실돼
    // 재저장 시 같은 상품이 중복 생성된다. throw 하지 않고 전체 결과를 반환한다.
    mutationFn: async (rows: SaveTarget[]): Promise<SaveRowResult[]> => {
      const results: SaveRowResult[] = []
      for (const [index, row] of rows.entries()) {
        try {
          if (row._isNew) {
            const res = await request.post<ApiResponseV2<{ itemId?: number }>, ProductBodyBase & { shipperCode: string }>(
              '/api/v1/master/products',
              { shipperCode: row.SPR_NM, ...toBody(row) },
            )
            results.push({ index, ok: true, id: res.data?.itemId ?? 0 })
          } else {
            await request.put<ApiResponseV2<unknown>, ProductBodyBase>(`/api/v1/master/products/${row._id}`, toBody(row))
            results.push({ index, ok: true, id: row._id })
          }
        } catch (error) {
          results.push({ index, ok: false, id: row._id, error })
        }
      }
      return results
    },
  })

// 1.0 grid/save D(MA02_DeleteForm MasterItem 삭제) 대응 — DELETE /{itemId}. 미저장 신규 행은 호출 대상 아님
export const useDeleteProducts = () =>
  useMutation({
    mutationFn: async (rows: SaveTarget[]) => {
      for (const row of rows.filter((r) => !r._isNew)) {
        await request.delete<ApiResponseV2<unknown>>(`/api/v1/master/products/${row._id}`)
      }
      return rows.length
    },
  })

// 엑셀 다운로드 — 1.0 파리티(필터 전체) + 2.0 서버 xlsx(EXPORT 감사 기록, GET /excel). 사유 모달 통과 후 호출.
// detailedReason(사유코드_상세사유) 은 BE 가 EXPORT 감사로그 remark 에 남긴다 (LOGISPRD-9602 공통 수용)
export const downloadProductsExcel = async (params: ProductManagementParams, detailedReason?: string) => {
  const qs = Object.entries({ ...buildSearchQuery(params), detailedReason })
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => (Array.isArray(v) ? v.map((x) => `${k}=${encodeURIComponent(String(x))}`).join('&') : `${k}=${encodeURIComponent(String(v))}`))
    .join('&')
  const res = await getExcel(`/api/v1/master/products/excel${qs ? `?${qs}` : ''}`)
  const disposition = String(res.headers?.['content-disposition'] ?? '')
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)/i.exec(disposition)
  const fileName = match ? decodeURIComponent(match[1]) : '상품관리.xlsx'
  const a = document.createElement('a')
  a.href = URL.createObjectURL(res.data)
  a.download = fileName
  a.click()
  URL.revokeObjectURL(a.href)
}
